/**
 *
 *  Become a Partner page
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 07/23/2024.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useState } from "react";
import { Title, Meta } from "react-meta-elements";

import { useAppState } from "../../../state";

// Components
import Header from "../../Header/Header";
import Footer from "../../Footer/Footer";
import Button from "../../Button/Button";
import AskConciergeModal from "../AskConciergeModal/AskConciergeModal";
import AppBanner from "../AppBanner/AppBanner";
import ScrollToTopOnMount from "../../ScrollToTopOnMount/ScrollToTopOnMount";

// Icons
import ArrowRightIcon from "../../../icons/ArrowRightIcon";

// Utils
import { clientWidth } from "../../../utils";

// Images
import Arrow from "../../BecomePartnerPage/Arrow";

import becomeAPartnerHero from "./hero.jpg";
import becomeAPartnerHero2x from "./hero@2x.jpg";
import becomeAPartnerHeroMobile from "./hero-mobile.jpg";
import becomeAPartnerHeroMobile2x from "./hero-mobile@2x.jpg";

import bannerTrainers from "./banner-trainers.jpg";
import bannerTrainers2x from "./banner-trainers@2x.jpg";
import bannerTrainersMobile from "./banner-trainers-mobile.jpg";
import bannerTrainersMobile2x from "./banner-trainers-mobile@2x.jpg";

import becomeAPartnerImg from "./become-a-partner-img.jpg";
import becomeAPartnerImg2x from "./become-a-partner-img@2x.jpg";
import becomeAPartnerImg3x from "./become-a-partner-img@3x.jpg";

import becomeAPartnerImgMobile from "./become-a-partner-img.jpg";
import becomeAPartnerImgMobile2x from "./become-a-partner-img@2x.jpg";
import becomeAPartnerImgMobile3x from "./become-a-partner-img@3x.jpg";

import TruBeLogo from "../../Homepage/TruBe";
import ThumbsUp from "./ThumbsUp";
import Testimonials from "../../Testimonials/Testimonials";
import askConcierge from "../../Homepage/AskConciergeModal/ask-concierge.svg";
import Pound from "./Pound";
import Calendar from "./Calendar";
import LocationMark from "./LocationMark";
import InvoiceCheck from "./InvoiceCheck";
import HealthAndSafety from "./HealthAndSafety";
import Idea from "./Idea";
import PoundShield from "./PoundShield";
import CustomerSupport from "./CustomerSupport";
import Cup from "./Cup";

import sparkles from "./sparkles.svg";

const OnboardingProcessStep = ({ className, step, text: TextElement }) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  return (
    <div className={`${className ? `${className} ` : ""}`}>
      <div
        className={`tbk-relative tbk-mx-auto tbk-flex tbk-h-auto tbk-cursor-pointer tbk-flex-col tbk-items-center tbk-justify-center tbk-rounded-4xl tbk-border-2 tbk-border tbk-border-basic-white tbk-bg-basic-white tbk-shadow-trube lg:tbk-h-44 lg:tbk-w-44 lg:hover:tbk-border-coral lg:hover:tbk-bg-coral xl:tbk-h-56 xl:tbk-w-56`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="tbk-w-full tbk-pt-full" />
        <div
          className={`md:tbk-text-cta tbk-text-small-bold tbk-absolute tbk-top-2 tbk-mb-2 tbk-block tbk-shrink-0 tbk-whitespace-nowrap tbk-py-0.5 tbk-py-0.5 tbk-px-1.5 tbk-uppercase md:tbk-top-1 lg:tbk-top-1 ${
            isHovered ? "tbk-text-basic-white" : "tbk-text-blue-grey"
          } lg:tbk-mb-1 lg:tbk-mb-2 xl:tbk-mb-2`}
        >
          Step {step}
        </div>
        <div className="tbk-px-1_ tbk-absolute tbk-min-w-full tbk-flex-col tbk-rounded-2xl tbk-text-center lg:tbk-mb-0 lg:tbk-pb-2 lg:tbk-pt-2 xl:tbk-px-2">
          <div
            className={`tbk-text-h3-subtitle md:tbk-text-title-bold tbk-mb-3_ tbk-text-center ${
              isHovered ? "tbk-text-basic-white" : "tbk-text-coral"
            }`}
          >
            {TextElement}
          </div>
        </div>
        {step === 5 ? (
          <img
            src={sparkles}
            className="tbk-absolute tbk-bottom-1 md:tbk-bottom-2.5"
            alt="Ask TruBe Concierge"
          />
        ) : null}
      </div>
    </div>
  );
};

export default function BecomePartnerPage() {
  const { user } = useAppState();

  const [isAskConciergeModalOpen, setIsAskConciergeModalOpen] = useState(false);

  const heroRef = React.useRef(null);

  const isUserLoggedIn = !!(user && user.id);

  return (
    <>
      <ScrollToTopOnMount />
      <Title>TruBe: Welcome to Team TruBe</Title>
      <Meta
        name="description"
        content="Join TruBe as a trainer and thrive with our innovative platform, comprehensive certification program, and exciting opportunities in fitness and wellbeing."
      />
      <Header
        showRegionSelect
        behaviour="none"
        isFixed
        isLoggedIn={!!(user && user.id)}
        heroRef={heroRef}
      />

      <div
        ref={heroRef}
        className="tbk-relative tbk-flex tbk-h-140 tbk-min-h-[644px] tbk-items-end tbk-pt-7 md:tbk-h-auto lg:tbk-min-h-[462px]"
      >
        <div className="tbk-absolute tbk-right-0 tbk-bottom-0 tbk-top-7 tbk-left-0 tbk-w-full tbk-overflow-hidden tbk-bg-grey-light">
          <picture className="tbk-h-full">
            <source
              media="(min-width: 768px)"
              srcSet={`${becomeAPartnerHero} 1x, ${becomeAPartnerHero2x} 2x,`}
            />
            <source
              srcSet={`${becomeAPartnerHeroMobile} 1x, ${becomeAPartnerHeroMobile2x} 2x,`}
            />

            <img
              className="tbk-min-h-full tbk-w-full tbk-object-cover md:tbk-h-full"
              src={becomeAPartnerHero}
              alt="Get more fitness clients"
            />
          </picture>
        </div>
        <div className="tbk-container tbk-z-1 tbk-mx-auto tbk-px-2 lg:tbk-px-2">
          <div className="tbk-relative tbk-h-full tbk-pt-7 tbk-pb-7 md:tbk-grid md:tbk-grid-cols-become-partner-hero lg:tbk-pb-8 lg:tbk-pt-7">
            <div>
              <div className="tbk-text-h2-black lg:tbk-text-epic-medium tbk-mb-2 !tbk-leading-tight tbk-text-basic-white lg:tbk-whitespace-nowrap lg:!tbk-leading-none">
                Become a TruBe
                <br className="md:tbk-hidden" /> Partner
              </div>
              <p className="tbk-text-main tbk-mb-4 tbk-text-basic-white">
                Join UK’s leading on-demand fitness and wellness provider! We
                are always interested in working with new talents across the UK.
                Find out how you can be part of something bigger.
              </p>
              <a
                href={"https://share.hsforms.com/18DtoRmCATuWpaGrrq9nVLwnv2ly"}
                target="_blank"
              >
                <Button
                  variant="secondary"
                  className="tbk-w-44"
                  endIcon={<ArrowRightIcon />}
                >
                  Apply Now
                </Button>
              </a>
            </div>
            <div></div>
          </div>
        </div>
      </div>

      <div className="tbk-pt-7 tbk-pb-7 lg:tbk-pt-8 lg:tbk-pb-7">
        <div className="tbk-container tbk-mx-auto tbk-px-2 lg:tbk-px-8">
          <div className="tbk-grid tbk-items-start tbk-gap-6 lg:tbk-grid-cols-2 lg:tbk-gap-8">
            <div className="tbk-mb-1 lg:tbk-mb-0">
              <h2 className="tbk-text-h2-black lg:tbk-text-epic-medium tbk-mb-2 !tbk-leading-tight tbk-text-primary lg:tbk-mb-4 lg:!tbk-leading-none">
                Be a TruBe Trainer
              </h2>
              <p className="tbk-text-main tbk-mb-5 tbk-text-primary lg:tbk-mb-7">
                Becoming a TruBe trainer offers unparalleled benefits. You'll
                create new opportunities, increase your income, and become the
                best trainer possible with unmatched support and infrastructure.
                TruBe manages everything for you, allowing you to focus on what
                you do best—transforming lives. Join TruBe and elevate your
                career to new heights.
              </p>

              <div className="tbk-text-center lg:tbk-text-left">
                <a
                  href={
                    "https://share.hsforms.com/18DtoRmCATuWpaGrrq9nVLwnv2ly"
                  }
                  target="_blank"
                >
                  <Button
                    size={"md"}
                    className="tbk-mt-1 tbk-mb-1 tbk-inline-flex tbk-w-44 lg:tbk-mt-0 lg:tbk-mb-0"
                    endIcon={<ArrowRightIcon className="tbk-ml-0.5" />}
                  >
                    Apply Now
                  </Button>
                </a>
              </div>
            </div>
            <div className="tbk--order-1_ lg:tbk-order-1_ tbk-relative lg:tbk-h-auto lg:tbk-bg-none lg:tbk-px-2">
              {/*<div className="lg:tbk-hidden_ tbk-pt-full" />*/}
              <picture className="tbk-bottom-0 tbk-left-0 tbk-right-0 tbk-top-0 tbk-grow lg:tbk-absolute">
                <source
                  media="(min-width: 768px)"
                  srcSet={`${becomeAPartnerImg} 1x, ${becomeAPartnerImg2x} 2x, ${becomeAPartnerImg2x} 3x`}
                />
                <source
                  srcSet={`${becomeAPartnerImgMobile} 1x, ${becomeAPartnerImgMobile2x} 2x, ${becomeAPartnerImgMobile2x} 3x`}
                />

                <img
                  className="tbk-w-full tbk-rounded-2xl tbk-object-cover"
                  src={becomeAPartnerImg}
                  alt="Find more fitness customers"
                />
              </picture>
            </div>
          </div>
        </div>
      </div>

      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-bg-basic-white tbk-pt-7 tbk-pb-7 lg:tbk-pb-8">
        <div className="tbk-container tbk-px-2 lg:tbk-px-8 lg:tbk-pb-5">
          <div className="tbk-relative tbk-mb-6 tbk-text-center">
            <h3 className="tbk-text-title-bold tbk-inline-block tbk-bg-basic-white tbk-px-2 tbk-text-center tbk-text-blue-grey">
              What We Require from You:
            </h3>
            <hr className="tbk-mb-0 tbk--mt-2 tbk-border-blue-grey-light" />
          </div>
          <div className="md:tbk-grid md:tbk-grid-cols-3 md:tbk-items-start md:tbk-gap-3">
            <div
              className="tbk-mb-2 tbk-flex tbk-h-full tbk-rounded-2xl tbk-p-2 tbk-shadow-trube lg:tbk-mb-0 lg:tbk-p-2"
              style={{ minHeight: "84px " }}
            >
              <div className="tbk-ml-0.5 tbk-mr-1.5 tbk-mt-1 tbk-h-1.5 tbk-w-1.5 tbk-shrink-0 tbk-rounded-full tbk-border tbk-border-solid tbk-border-coral tbk-bg-coral" />
              <div className="tbk-text-h3-subtitle tbk-text-grey-main">
                Eligible to work in the UK
              </div>
            </div>

            <div
              className="tbk-mb-2 tbk-flex tbk-h-full tbk-rounded-2xl tbk-p-2 tbk-shadow-trube lg:tbk-mb-0 lg:tbk-p-2"
              style={{ minHeight: "84px " }}
            >
              <div className="tbk-ml-0.5 tbk-mr-1.5 tbk-mt-1 tbk-h-1.5 tbk-w-1.5 tbk-shrink-0 tbk-rounded-full tbk-border tbk-border-solid tbk-border-coral tbk-bg-coral" />
              <div className="tbk-text-h3-subtitle tbk-text-grey-main">
                Have public liability Insurance
              </div>
            </div>

            <div
              className="tbk-mb-2 tbk-flex tbk-h-full tbk-rounded-2xl tbk-p-2 tbk-shadow-trube lg:tbk-mb-0 lg:tbk-p-2"
              style={{ minHeight: "84px " }}
            >
              <div className="tbk-ml-0.5 tbk-mr-1.5 tbk-mt-1 tbk-h-1.5 tbk-w-1.5 tbk-shrink-0 tbk-rounded-full tbk-border tbk-border-solid tbk-border-coral tbk-bg-coral" />
              <div className="tbk-text-h3-subtitle tbk-text-grey-main">
                Certification in your field of expertise
              </div>
            </div>

            <div
              className="tbk-mb-2 tbk-flex tbk-h-full tbk-rounded-2xl tbk-p-2 tbk-shadow-trube lg:tbk-mb-0 lg:tbk-p-2"
              style={{ minHeight: "84px " }}
            >
              <div className="tbk-ml-0.5 tbk-mr-1.5 tbk-mt-1 tbk-h-1.5 tbk-w-1.5 tbk-shrink-0 tbk-rounded-full tbk-border tbk-border-solid tbk-border-coral tbk-bg-coral" />
              <div className="tbk-text-h3-subtitle tbk-text-grey-main">
                Experience in teaching private sessions as well as groups
              </div>
            </div>

            <div
              className="tbk-mb-2 tbk-flex tbk-h-full tbk-rounded-2xl tbk-p-2 tbk-shadow-trube lg:tbk-mb-0 lg:tbk-p-2"
              style={{ minHeight: "84px " }}
            >
              <div className="tbk-ml-0.5 tbk-mr-1.5 tbk-mt-1 tbk-h-1.5 tbk-w-1.5 tbk-shrink-0 tbk-rounded-full tbk-border tbk-border-solid tbk-border-coral tbk-bg-coral" />
              <div className="tbk-text-h3-subtitle tbk-text-grey-main">
                Highly professional and driven
              </div>
            </div>

            <div
              className="tbk-flex tbk-h-full tbk-rounded-2xl tbk-p-2 tbk-shadow-trube lg:tbk-mb-0 lg:tbk-p-2"
              style={{ minHeight: "84px " }}
            >
              <div className="tbk-ml-0.5 tbk-mr-1.5 tbk-mt-1 tbk-h-1.5 tbk-w-1.5 tbk-shrink-0 tbk-rounded-full tbk-border tbk-border-solid tbk-border-coral tbk-bg-coral" />
              <div className="tbk-text-h3-subtitle tbk-text-grey-main">
                Empathetic and customer focused
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="tbk-container tbk-mx-auto tbk-px-2 tbk-pt-7 tbk-pb-7 lg:tbk-px-8 lg:tbk-py-30">
        <div className="lg:tbk-grid lg:tbk-grid-cols-become-partner-layout lg:tbk-gap-5 xl:tbk-gap-7">
          <div className="tbk-text-epic-medium lg:tbk-text-epic-bold xl:tbk-text-extra-bold tbk-mb-2 !tbk-leading-none tbk-text-blue-grey lg:tbk-mb-0 xl:!tbk-leading-slight">
            What <span className="tbk-text-coral">TruBe</span> Means to{" "}
            <span className="tbk-text-coral">You</span>
          </div>
          <p className="tbk-text-main lg:tbk-text-h3-subtitle tbk-text-primary">
            At TruBe, we value and empower every trainer and therapist. You'll
            be encouraged to infuse your unique teaching style into each
            session, celebrating growth and diversity. As a part of TruBe,
            you'll have the opportunity to shine in PR activities and
            participate in exciting events. Join us and elevate your journey to
            new heights!
          </p>
        </div>
      </div>

      <div className="tbk-container tbk-mx-auto tbk-mb-7 tbk-px-4 lg:tbk-pt-7 lg:tbk-pb-30">
        <div className="tbk-relative tbk-mb-6 tbk-text-center">
          <h3 className="tbk-text-title-bold tbk-inline-block tbk-bg-basic-white tbk-px-2 tbk-text-center tbk-text-blue-grey">
            The Benefits of TruBe
          </h3>
          <hr className="tbk-mb-0 tbk--mt-2 tbk-border-blue-grey-light" />
        </div>
        <div className="tbk-mb-7 tbk-grid tbk-grid-cols-2 tbk-gap-2 lg:tbk-mb-8 lg:tbk-grid-cols-5 lg:tbk-gap-4">
          <div className="tbk-cursor-pointer tbk-rounded-2xl tbk-py-2 tbk-text-center hover:tbk-bg-basic-white hover:tbk-shadow-trube">
            <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center ">
              <ThumbsUp className="tbk-mb-2" />
              <h3 className="tbk-text-h3-subtitle tbk-mb-2">No joining fee</h3>
            </div>
          </div>
          <div className="tbk-cursor-pointer tbk-rounded-2xl tbk-py-2 tbk-text-center hover:tbk-bg-basic-white hover:tbk-shadow-trube">
            <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center ">
              <Pound className="tbk-mb-2" />
              <h3 className="tbk-text-h3-subtitle tbk-mb-2">
                No weekly or monthly retainer
              </h3>
            </div>
          </div>
          <div className="tbk-cursor-pointer tbk-rounded-2xl tbk-py-2 tbk-text-center hover:tbk-bg-basic-white hover:tbk-shadow-trube">
            <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center ">
              <Calendar className="tbk-mb-2" />
              <h3 className="tbk-text-h3-subtitle tbk-mb-2">
                Flexible scheduling to your availability
              </h3>
            </div>
          </div>
          <div className="tbk-cursor-pointer tbk-rounded-2xl tbk-py-2 tbk-text-center hover:tbk-bg-basic-white hover:tbk-shadow-trube">
            <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center ">
              <LocationMark className="tbk-mb-2" />
              <h3 className="tbk-text-h3-subtitle tbk-mb-2">
                Clients in your chosen locations
              </h3>
            </div>
          </div>
          <div className="tbk-cursor-pointer tbk-rounded-2xl tbk-py-2 tbk-text-center hover:tbk-bg-basic-white hover:tbk-shadow-trube">
            <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center ">
              <InvoiceCheck className="tbk-mb-2" />
              <h3 className="tbk-text-h3-subtitle tbk-mb-2">
                No invoicing
                <br />
                required
              </h3>
            </div>
          </div>
          <div className="tbk-cursor-pointer tbk-rounded-2xl tbk-py-2 tbk-text-center hover:tbk-bg-basic-white hover:tbk-shadow-trube">
            <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center ">
              <HealthAndSafety className="tbk-mb-2" />
              <h3 className="tbk-text-h3-subtitle tbk-mb-2">
                Safety 1st work
                <br />
                ethic
              </h3>
            </div>
          </div>
          <div className="tbk-cursor-pointer tbk-rounded-2xl tbk-py-2 tbk-text-center hover:tbk-bg-basic-white hover:tbk-shadow-trube">
            <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center ">
              <Idea className="tbk-mb-2" />
              <h3 className="tbk-text-h3-subtitle tbk-mb-2">
                Customer
                <br />
                Insights
              </h3>
            </div>
          </div>
          <div className="tbk-cursor-pointer tbk-rounded-2xl tbk-py-2 tbk-text-center hover:tbk-bg-basic-white hover:tbk-shadow-trube">
            <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center ">
              <PoundShield className="tbk-mb-2" />
              <h3 className="tbk-text-h3-subtitle tbk-mb-2">
                Payment for client missed/cancelled sessions
              </h3>
            </div>
          </div>
          <div className="tbk-cursor-pointer tbk-rounded-2xl tbk-py-2 tbk-text-center hover:tbk-bg-basic-white hover:tbk-shadow-trube">
            <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center ">
              <CustomerSupport className="tbk-mb-2" />
              <h3 className="tbk-text-h3-subtitle tbk-mb-2">
                Customer service
                <br />
                team
              </h3>
            </div>
          </div>
          <div className="tbk-cursor-pointer tbk-rounded-2xl tbk-py-2 tbk-text-center hover:tbk-bg-basic-white hover:tbk-shadow-trube">
            <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center ">
              <Cup className="tbk-mb-2" />
              <h3 className="tbk-text-h3-subtitle tbk-mb-2">
                The ability to focus on just the session
              </h3>
            </div>
          </div>
        </div>

        <a
          href={"https://share.hsforms.com/18DtoRmCATuWpaGrrq9nVLwnv2ly"}
          target="_blank"
        >
          <Button
            size="md"
            className="tbk-mx-auto tbk-w-44"
            endIcon={<ArrowRightIcon />}
          >
            Apply now
          </Button>
        </a>
      </div>

      <div className="tbk-relative">
        <div className="tbk-absolute tbk-top-0 tbk-left-0 tbk-h-full tbk-w-full">
          <picture className="tbk-h-full tbk-w-full">
            <source
              media="(min-width: 768px)"
              srcSet={`${bannerTrainers} 1x, ${bannerTrainers2x} 2x,`}
            />
            <source
              srcSet={`${bannerTrainersMobile} 1x, ${bannerTrainersMobile2x} 2x,`}
            />

            <img
              className="tbk-h-full tbk-w-full tbk-object-cover tbk-object-top"
              src={bannerTrainers}
              alt="Best Personal Trainer Company"
            />
          </picture>
        </div>

        <div className="tbk-relative tbk-z-1 tbk-mx-auto tbk-flex tbk-min-h-[409px] tbk-max-w-[804px] tbk-max-w-[804px] tbk-flex-col tbk-items-center tbk-justify-center tbk-px-2 tbk-px-2 lg:tbk-min-h-[528px]">
          <div className="tbk-text-h2-black lg:tbk-text-epic-bold tbk-mb-2 tbk-text-center !tbk-leading-tight tbk-text-basic-white lg:tbk-mb-2">
            "We value and empower every trainer & therapist"
          </div>
          <TruBeLogo />
        </div>
      </div>

      <div className="tbk-bg-coral_ tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pt-8 tbk-pb-7 lg:tbk-pb-28">
        <div className="tbk-container tbk-px-3 lg:tbk-px-2">
          <div className="tbk-relative tbk-mb-6 tbk-text-center">
            <h3 className="tbk-text-title-bold tbk-inline-block tbk-bg-basic-white tbk-px-2 tbk-text-center tbk-text-blue-grey">
              Our Simple Onboarding Process
            </h3>
            <hr className="tbk-mb-0 tbk--mt-2 tbk-border-blue-grey-light" />
          </div>
          <div className="tbk-grid tbk-grid-cols-2 tbk-flex-wrap tbk-gap-4 lg:tbk-mb-1 lg:tbk-grid-cols-5 lg:tbk-items-start">
            <div className="tbk-relative">
              <OnboardingProcessStep
                step={1}
                text={"Apply Now"}
                className="tbk-mt-3"
              />

              <Arrow className="tbk-absolute tbk--right-3 tbk-top-1/2 tbk-mt-1.5 tbk-mb-3 tbk--translate-y-1/2 lg:tbk--right-4 lg:tbk-rotate-0 xl:tbk-mx-1" />
            </div>
            <div className="tbk-relative">
              <OnboardingProcessStep
                step={2}
                text={
                  <div>
                    Submit
                    <br className="lg:tbk-hidden" /> Documents
                  </div>
                }
                className="tbk-mt-3"
              />

              <div className="tbk-absolute tbk--bottom-4 tbk-left-1/2 tbk-h-2 tbk-w-[calc(50%_+_17px)] tbk--translate-x-full tbk-rounded-br-lg tbk-border-r-2 tbk-border-b-2 tbk-border-blue-grey lg:tbk-hidden">
                <div
                  className="tbk-absolute tbk-rounded-full tbk-bg-blue-grey"
                  style={{
                    top: "-1px",
                    right: "-2px",
                    width: "2px",
                    height: "2px",
                  }}
                ></div>
              </div>

              <Arrow className="tbk-absolute tbk--right-3 tbk-top-1/2 tbk-mb-3 tbk-mt-1.5 tbk-hidden tbk--translate-y-1/2 lg:tbk--right-4 lg:tbk-block lg:tbk-rotate-0 xl:tbk-mx-1" />
            </div>

            <div className="tbk-relative">
              <div className="tbk-absolute tbk--top-[2px] tbk-right-0 tbk-h-2 tbk-w-[calc(50%_+_17px)] tbk-translate-x-2 tbk-rounded-tl-lg tbk-border-l-2 tbk-border-t-2 tbk-border-blue-grey lg:tbk-hidden">
                <div
                  className="tbk-absolute tbk-top-0.5 tbk--bottom-4 tbk-rotate-45 tbk-rounded-br-sm tbk-border-r-2 tbk-border-b-2 tbk-border-blue-grey"
                  style={{ width: "10px", height: "10px", left: "-6px" }}
                >
                  <div
                    className="tbk-absolute tbk-rounded-full tbk-bg-blue-grey"
                    style={{
                      top: "-1px",
                      right: "-2px",
                      width: "2px",
                      height: "2px",
                    }}
                  ></div>
                  <div
                    className="tbk-absolute tbk-rounded-full tbk-bg-blue-grey"
                    style={{
                      bottom: "-2px",
                      right: "7px",
                      width: "2px",
                      height: "2px",
                    }}
                  ></div>
                </div>
              </div>

              <OnboardingProcessStep
                step={3}
                text={"One-to-One Interview"}
                className="tbk-mt-3"
              />

              <Arrow className="tbk-absolute tbk--right-3 tbk-top-1/2 tbk-mt-1.5 tbk-mb-3 tbk--translate-y-1/2 lg:tbk--right-4 lg:tbk-rotate-0 xl:tbk-mx-1" />
            </div>

            <div className="tbk-relative">
              <OnboardingProcessStep
                step={4}
                text={"Branding Workshop"}
                className="tbk-mt-3"
              />
              <div className="tbk-border-2_ tbk-absolute tbk--bottom-4 tbk-left-1/2 tbk-h-2 tbk-w-[calc(25%_+_17px)] tbk--translate-x-full tbk-rounded-br-lg tbk-border-r-2 tbk-border-b-2 tbk-border-blue-grey lg:tbk-hidden">
                <div
                  className="tbk-absolute tbk-rounded-full tbk-bg-blue-grey"
                  style={{
                    top: "-1px",
                    right: "-2px",
                    width: "2px",
                    height: "2px",
                  }}
                ></div>
              </div>

              <Arrow className="tbk-absolute tbk--right-3 tbk-top-1/2 tbk-mb-3 tbk-mt-1.5 tbk-hidden tbk--translate-y-1/2 lg:tbk--right-4 lg:tbk-block lg:tbk-rotate-0 xl:tbk-mx-1" />
            </div>

            <div className="tbk-relative">
              <div className="tbk-absolute tbk--top-[2px] tbk--right-1 tbk-h-2 tbk-w-[calc(25%_+_17px)] tbk-translate-x-full tbk-rounded-tl-lg tbk-border-l-2 tbk-border-t-2 tbk-border-blue-grey lg:tbk-hidden">
                <div
                  className="tbk-absolute tbk-left-0.5 tbk-top-0.5 tbk--bottom-4 tbk-rotate-45 tbk-rounded-br-sm tbk-border-r-2 tbk-border-b-2 tbk-border-blue-grey"
                  style={{ width: "10px", height: "10px", left: "-6px" }}
                >
                  <div
                    className="tbk-absolute tbk-rounded-full tbk-bg-blue-grey"
                    style={{
                      top: "-1px",
                      right: "-2px",
                      width: "2px",
                      height: "2px",
                    }}
                  ></div>
                  <div
                    className="tbk-absolute tbk-rounded-full tbk-bg-blue-grey"
                    style={{
                      bottom: "-2px",
                      right: "7px",
                      width: "2px",
                      height: "2px",
                    }}
                  ></div>
                </div>
              </div>
              <OnboardingProcessStep
                step={5}
                text={"Time to Shine"}
                className="tbk-relative tbk-left-1/2 tbk-mt-3 tbk-translate-x-2 lg:tbk-left-auto lg:tbk-translate-x-0"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="tbk-relative tbk-bg-basic-white tbk-pt-4 lg:tbk-mb-0 lg:tbk-pt-9 ">
        <div className="tbk-container tbk-z-1 tbk-mx-auto tbk-px-2 lg:tbk-px-8 lg:tbk-pb-2">
          <div className="tbk-relative tbk-mb-2 tbk-text-center">
            <h3 className="tbk-text-h3-subtitle lg:tbk-text-title-bold tbk-inline-block tbk-bg-basic-white tbk-px-2 tbk-text-center tbk-text-blue-grey">
              Testimonials
            </h3>
            <hr className="tbk-mb-0 tbk--mt-2 tbk-border-blue-grey-light" />
          </div>
        </div>
      </div>

      <div className="tbk-bg-basic-white tbk-pb-24 lg:tbk-pb-24">
        <Testimonials />
      </div>

      <div
        className={`tbk-container tbk-relative tbk-mx-auto tbk-justify-center tbk-px-2 tbk-pt-7 tbk-pb-7 md:tbk-px-4 md:tbk-pt-7 md:tbk-pb-7`}
      >
        <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-p-2 md:tbk-p-3">
          <div className="tbk-text-center tbk-text-grey-inactive">
            <img
              src={askConcierge}
              className="tbk-mb-4 tbk-inline-block tbk-h-7 tbk-w-7"
              alt="Ask TruBe Concierge"
            />
            <p className="tbk-text-epic-medium md:tbk-text-title-bold tbk-my-4 tbk-text-center tbk-text-primary">
              Having any <span className="tbk-text-coral">Questions</span>?
              <br />
              <span className="tbk-text-coral">Ask Us</span>, We're Here to
              Help.
            </p>
          </div>
          <Button
            size={"md"}
            endIcon={<ArrowRightIcon />}
            onClick={() => setIsAskConciergeModalOpen(true)}
          >
            Ask a Question
          </Button>
        </div>
      </div>

      <AppBanner />

      <AskConciergeModal
        text="Have any questions about being a TruBe Coach? Contact us here"
        email="operations@trubeapp.com"
        isOpen={isAskConciergeModalOpen}
        onClose={() => setIsAskConciergeModalOpen(false)}
      />

      <Footer />
    </>
  );
}
